import React from 'react';
import { InfoIcon } from 'lucide-react';
import { AuthInputTextField, AuthSeparator, AuthInputDisabledField } from '../..';
import { LockedField } from '@/shared/components/locked-field';
import { useLockedOrgLabel } from '@/shared/hooks/use-locked-org-label';
import { CustomDisclosuresEditor } from './CustomDisclosuresEditor';
import { ChangeEmailDialog } from './ChangeEmailDialog';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';

interface ProfileTabProps {
  advisorState: AdvisorProfile;
  errors: Partial<Record<keyof AdvisorProfile, boolean>>;
  handleAdvisorStateChange: (
    advisorKey: keyof AdvisorProfile,
  ) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePhoneNumberChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddressFieldChange: (field: keyof Address, value: string) => void;
  handleCustomDisclosuresChange: (value: string) => void;
  onEmailChanged: (newEmail: string) => void;
}

export function ProfileTab({
  advisorState,
  errors,
  handleAdvisorStateChange,
  handlePhoneNumberChange,
  handleAddressFieldChange,
  handleCustomDisclosuresChange,
  onEmailChanged,
}: ProfileTabProps) {
  const lockedOrgLabel = useLockedOrgLabel();
  const isOrgLocked = lockedOrgLabel !== null;
  const isEdjPilot = useIsEdjPilotAdvisor();

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
      <AuthSeparator
        text="Login Information"
        className="sm:col-span-6"
      />
      <div className="mt-0 flex items-end gap-3 sm:col-span-4">
        <AuthInputDisabledField
          id="email"
          label="Email address"
          value={advisorState.advisorEmail}
          className="mt-0 flex-1"
        />
        {!isEdjPilot && (
          <ChangeEmailDialog
            advisorId={advisorState.advisorId}
            currentEmail={advisorState.advisorEmail}
            onEmailChanged={onEmailChanged}
          />
        )}
      </div>
      {advisorState.advisorPhoneNumber && (
        <AuthInputDisabledField
          id="login-phone-number"
          label="SMS verification phone number"
          value={advisorState.advisorPhoneNumber}
          className="mt-0 sm:col-span-2"
        />
      )}
      <div className="-mt-2 sm:col-span-6">
        <div className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600 ">
          <InfoIcon className="size-4 flex-shrink-0 text-slate-500" />
          <span>
            To change your login phone number, please contact{' '}
            <a
              href="mailto:hello@joinwaterlily.com"
              className="hover:text-primary hover:decoration-primary font-medium text-slate-800 underline decoration-slate-400 underline-offset-2 transition-colors"
            >
              hello@joinwaterlily.com
            </a>
          </span>
        </div>
      </div>
      <AuthSeparator
        text="Advisor Information"
        className="sm:col-span-6"
      />
      <AuthInputTextField
        id="first-name"
        label="First name"
        value={advisorState.advisorFirstName}
        onChange={handleAdvisorStateChange('advisorFirstName')}
        autoComplete="given-name"
        className="mt-0 sm:col-span-3"
        error={errors.advisorFirstName}
      />
      <AuthInputTextField
        id="last-name"
        label="Last name"
        value={advisorState.advisorLastName}
        onChange={handleAdvisorStateChange('advisorLastName')}
        autoComplete="family-name"
        className="mt-0 sm:col-span-3"
        error={errors.advisorLastName}
      />

      <LockedField
        active={isOrgLocked}
        tooltipText={`Organization legal name is preset for ${lockedOrgLabel}.`}
        className="mt-0 sm:col-span-4"
      >
        <AuthInputTextField
          id="organizationName"
          label="Organization Name (Legal)"
          value={advisorState.organizationName ?? ''}
          onChange={handleAdvisorStateChange('organizationName')}
          error={errors.organizationName}
          className="mt-0 sm:col-span-4"
        />
      </LockedField>
      <LockedField
        active={isOrgLocked}
        tooltipText={`Organization name is preset for ${lockedOrgLabel}.`}
        className="mt-0 sm:col-span-4"
      >
        <AuthInputTextField
          id="organizationDisplayName"
          label="Organization Name (Whitelabel)"
          value={advisorState.organizationDisplayName ?? ''}
          onChange={handleAdvisorStateChange('organizationDisplayName')}
          error={errors.organizationDisplayName}
          className="mt-0 sm:col-span-4"
        />
      </LockedField>

      <AuthInputTextField
        id="business-phone-number"
        label="Business phone number"
        value={advisorState.advisorBusinessPhoneNumber}
        onChange={handlePhoneNumberChange}
        className="mt-0 sm:col-span-3"
        required={false}
        error={errors.advisorBusinessPhoneNumber}
        placeholder="(555) 123-4567"
      />
      <AuthSeparator
        text="Custom Disclosures"
        className="sm:col-span-6"
      />
      <div className="sm:col-span-6">
        <CustomDisclosuresEditor
          id="custom-disclosures"
          label="Your disclosures (optional)"
          description={`These will appear in addition to Waterlily's disclosures in client PDFs, titled "${advisorState.organizationName?.trim() || 'Advisor'} Disclosures". Highlight text and click Add link to include a hyperlink.`}
          value={advisorState.customDisclosures ?? ''}
          onChange={handleCustomDisclosuresChange}
          className="mt-0"
        />
      </div>
      <AuthSeparator
        text="Business Address"
        className="sm:col-span-6"
      />

      <AuthInputTextField
        id="business-street-address"
        label="Street Address"
        value={advisorState.advisorBusinessAddress?.streetAddress || ''}
        onChange={e => handleAddressFieldChange('streetAddress', e.target.value)}
        className="mt-0 sm:col-span-4"
        required={false}
        placeholder="123 Main Street"
      />

      <AuthInputTextField
        id="business-apartment"
        label="Apartment, suite, etc."
        value={advisorState.advisorBusinessAddress?.apartment || ''}
        onChange={e => handleAddressFieldChange('apartment', e.target.value)}
        className="mt-0 sm:col-span-2"
        required={false}
        placeholder="Suite 200"
      />

      <AuthInputTextField
        id="business-city"
        label="City"
        value={advisorState.advisorBusinessAddress?.city || ''}
        onChange={e => handleAddressFieldChange('city', e.target.value)}
        className="mt-0 sm:col-span-2"
        required={false}
        placeholder="San Francisco"
      />

      <AuthInputTextField
        id="business-state"
        label="State Abbreviation"
        value={advisorState.advisorBusinessAddress?.state || ''}
        onChange={e => handleAddressFieldChange('state', e.target.value.toUpperCase())}
        className="mt-0 sm:col-span-2"
        required={false}
        placeholder="CA"
      />

      <AuthInputTextField
        id="business-zip-code"
        label="ZIP Code"
        value={advisorState.advisorBusinessAddress?.zipCode || ''}
        onChange={e => {
          const value = e.target.value.replace(/[^\d-]/g, ''); // Only allow digits and hyphen
          handleAddressFieldChange('zipCode', value);
        }}
        className="mt-0 sm:col-span-2"
        required={false}
        placeholder="94105"
      />

      <AuthInputTextField
        id="business-country"
        label="Country"
        value={advisorState.advisorBusinessAddress?.country}
        onChange={e => handleAddressFieldChange('country', e.target.value)}
        className="mt-0 sm:col-span-2"
        required={false}
        placeholder="US"
      />
    </div>
  );
}
